import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { Button, Dropdown } from "react-bootstrap"

import Spinner from "../../components/Spinner"
import DisplayErrorMessage from "../../components/DisplayErrorMessage"
import { useClearClubNotificationsMutation, useClubNotificationsReadMutation, useFetchClubNotificationsQuery, useDeleteClubNotificationMutation } from "../../app/clubsApiSlice"
import FormattedNotification from "../../components/notifications/FormattedNotification"
import ButtonWithTooltip from "../../components/buttons/ButtonWithTooltip"
import { SquareCheckBig } from "lucide-react"
import useAuth from "../../hooks/useAuth"

const clubNotifications = ({ clubId, clubName, notifications }) => {
  const { uid } = useAuth()
  const { data, isSuccess, isLoading, isError, error } = useFetchClubNotificationsQuery(clubId)
  const [clearClubNotifications] = useClearClubNotificationsMutation()
  const [deleteClubNotification] = useDeleteClubNotificationMutation()
  const [clubNotificationsRead] = useClubNotificationsReadMutation()

  const [view, setView] = useState("all")
  const [showDropdown, setShowDropdown] = useState(false)

  // Clear notifications when the component mounts
  useEffect(() => {
    if (notifications > 0) {
      clearClubNotifications({ clubId, uid })
    }
  }, [])

  // Calculate unread notifications
  const { unreadRequests, unreadMessages } = useMemo(() => {
    if (!data) return { unreadRequests: [], unreadMessages: [] }

    const unreadRequests = Object.entries(data?.requests || {})
      .filter(([_, request]) => !request.read)
      .map(([id]) => id)

    const unreadMessages = Object.entries(data?.messages || {})
      .filter(([_, message]) => !message.read)
      .map(([id]) => id)

    return { unreadRequests, unreadMessages }
  }, [data])

  const hasUnread = unreadRequests.length > 0 || unreadMessages.length > 0

  const markAllRead = () => {
    if (hasUnread) {
      clubNotificationsRead({ requests: unreadRequests, messages: unreadMessages, clubId })
    }
  }

  const markOneRead = (id, category) => {
    if (category === "requests") {
      clubNotificationsRead({ requests: [id], messages: [], clubId })
    } else if (category === "messages") {
      clubNotificationsRead({ requests: [], messages: [id], clubId })
    }
  }

  const handleDeleteNotification = async (data) => {
    try {
      await deleteClubNotification(data).unwrap()
    } catch (error) {
      console.log(error)
      toast.error("Error deleting notification")
    }
  }

  if (isLoading) return <Spinner />
  if (isError) {
    console.error(error || "An error occurred while loading notifications")
    return (
      <div className="pt-3">
        <DisplayErrorMessage>Error loading notifications</DisplayErrorMessage>
      </div>
    )
  }
  if (!isSuccess || !data) return null

  const messagesArray = Object.entries(data?.messages ?? {}).map(([id, messageData]) => ({
    id,
    category: "messages",
    ...messageData
  }))

  const requestsArray = Object.entries(data?.requests ?? {}).map(([id, requestData]) => ({
    id,
    category: "requests",
    ...requestData
  }))

  const mergedArray = [...messagesArray, ...requestsArray]

  const viewMap = {
    notifications: mergedArray,
    requests: requestsArray,
    messages: messagesArray
  }

  const chosenArray = viewMap[view] ?? mergedArray

  const sortedArray = chosenArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

  const viewNamesMap = {
    all: "notifications",
    requests: "requests",
    messages: "messages"
  }

  const listContent = sortedArray.length > 0 ? (
    <ul className="w-100 list-unstyled">
      {sortedArray.map((content, index) => {
        const { category, id } = content
        return (
          <FormattedNotification
            key={index}
            data={{
              ...content,
              clubName,
              clubId
            }}
            deleteNotification={() => handleDeleteNotification({ docId: id, clubId, category })}
            markRead={() => markOneRead(id, category)}
          />
        )
      })}
    </ul>
  ) : (
    <p>No {viewNamesMap[view]} to show</p>
  )

  const currentClass = "fw-semibold rounded-3 bg-primary bg-opacity-10 text-primary"
  const inactiveClass = "border-0 fw-semibold rounded-3 text-muted custom-hover"

  return (
    <div
      className="d-flex flex-column align-items-center shadow-sm rounded-4 pb-4 pt-3 px-2 mx-auto p-sm-4 bg-white"
      style={{ maxWidth: "100%", width: "680px" }}
    >
      <div className="position-relative w-100 mb-4">
        <h3 className="fw-bold text-center mb-0 mt-2">Notifications</h3>

        {/* Mark all as read button */}
        {hasUnread && (
          <ButtonWithTooltip text="Mark all as read">
            <Button onClick={markAllRead} variant="" className="ms-auto border-0 p-2 me-2 position-absolute top-0 end-0"><SquareCheckBig /></Button>
          </ButtonWithTooltip>
        )}
      </div>

      {/* Change view buttons */}
      <div className="justify-content-start w-100 gap-3 ps-2 d-none d-sm-flex">
        <Button onClick={() => setView("all")} variant="" className={view === "all" ? currentClass : inactiveClass}>All</Button>
        <Button onClick={() => setView("messages")} variant="" className={view === "messages" ? currentClass : inactiveClass}>Messages</Button>
        <Button onClick={() => setView("requests")} variant="" className={view === "requests" ? currentClass : inactiveClass}>Requests</Button>
      </div>

      {/* Dropdown menu for mobile */}
      <div className="d-flex w-100 gap-3 ps-2 d-sm-none">
        <Dropdown show={showDropdown} onToggle={(isOpen) => setShowDropdown(isOpen)} drop="down-end">
          <Dropdown.Toggle
            variant=''
            className={`border-0 ${currentClass} px-3 mb-2`}
          >
            View
          </Dropdown.Toggle>

          <Dropdown.Menu className="shadow border-0 px-2">
            <Dropdown.Item onClick={() => setView("all")} className={`${view === "all" && currentClass} ${inactiveClass} custom-hover mb-1`}>
              All
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setView("messages")} className={`${view === "messages" && currentClass} ${inactiveClass} custom-hover mb-1`}>
              Messages
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setView("requests")} className={`${view === "requests" && currentClass} ${inactiveClass} custom-hover`}>
              Requests
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <hr className="w-100 text-secondary mt-1" />
      {listContent}
    </div>
  )
}

export default clubNotifications